import { useCallback, useEffect, useState } from "react";
import type { LogFilters, SavedView } from "@/lib/types";

const STORAGE_KEY = "leif:saved-views";

function load(): SavedView[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SavedView[]) : [];
  } catch {
    return [];
  }
}

function save(views: SavedView[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(views));
  window.dispatchEvent(new CustomEvent("leif:saved-views-changed"));
}

function makeId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useSavedViews() {
  const [views, setViews] = useState<SavedView[]>(() => load());

  useEffect(() => {
    const onChange = () => setViews(load());
    window.addEventListener("leif:saved-views-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("leif:saved-views-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const create = useCallback((name: string, filters: LogFilters) => {
    const view: SavedView = { id: makeId(), name, filters, createdAt: new Date().toISOString() };
    save([...load(), view]);
    return view;
  }, []);

  const remove = useCallback((id: string) => {
    save(load().filter((v) => v.id !== id));
  }, []);

  const rename = useCallback((id: string, name: string) => {
    save(load().map((v) => (v.id === id ? { ...v, name } : v)));
  }, []);

  return { views, create, remove, rename };
}
